import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Flame, BookOpen, Scroll } from 'lucide-react';
export function AlexandriaSection() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-black via-stone-950 to-black overflow-hidden" data-id="element-85">
      {/* Ancient manuscript texture */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h60v60H0z' fill='none'/%3E%3Cpath d='M30 30m-20 0a20 20 0 1 0 40 0a20 20 0 1 0 -40 0' stroke='%23d4a574' stroke-width='0.5' fill='none' opacity='0.3'/%3E%3C/svg%3E")`,
      backgroundSize: '60px 60px'
    }} data-id="element-86" />

      {/* Warm candlelight glow */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-600/10 rounded-full blur-3xl" data-id="element-87" />
      <div className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl" data-id="element-88" />

      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 30
    }} animate={inView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.8
    }} className="relative z-10 max-w-4xl mx-auto" data-id="element-89">
        {/* Decorative header with scroll icon */}
        <div className="flex items-center justify-center gap-4 mb-12" data-id="element-90">
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-91" />
          <Scroll className="w-8 h-8 text-amber-500" data-id="element-92" />
          <div className="w-24 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-93" />
        </div>

        {/* Main headline */}
        <h2 className="text-2xl sm:text-4xl md:text-6xl font-serif text-center mb-8 sm:mb-12 leading-tight text-stone-100" style={{
        fontFamily: 'Cinzel, serif',
        textShadow: '0 0 30px rgba(217, 119, 6, 0.3)'
      }} data-id="element-94">
          For Centuries, the{' '}
          <span className="text-amber-500" style={{
          textShadow: '0 0 40px rgba(217, 119, 6, 0.5)'
        }} data-id="element-95">
            Great Library of Alexandria
          </span>{' '}
          Was One of the Most Important Places on Earth
        </h2>

        {/* Body content */}
        <div className="space-y-6 text-lg md:text-xl leading-relaxed text-stone-300" data-id="element-96">
          <p className="text-center text-xl font-semibold text-amber-400" data-id="element-97">
            The central "brain" for human civilization.
          </p>

          <p data-id="element-98">
            It held the concentrated secrets of engineering, astronomy,
            medicine, mathematics and more.
          </p>

          <p data-id="element-99">
            But then, perhaps due to an accidental fire in 48 BC (attributed to
            Julius Caesar's civil war)...
          </p>

          <p data-id="element-100">Or many other destructive events over the next 400 years...</p>

          <p className="text-stone-100 font-bold" data-id="element-101">
            It was eventually destroyed.
          </p>

          {/* Dramatic pause with fire icon */}
          <div className="flex justify-center py-8" data-id="element-102">
            <motion.div animate={{
            scale: [1, 1.1, 1],
            opacity: [0.7, 1, 0.7]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-103">
              <Flame className="w-12 h-12 text-orange-500" data-id="element-104" />
            </motion.div>
          </div>

          <p data-id="element-105">
            Knowledge which had been gathered, curated and passed down by humans
            across the ages...
          </p>

          <p data-id="element-106">Went from being in one central, organized location...</p>

          <p data-id="element-107">To being fragmented across the earth once more.</p>

          <p className="text-2xl font-bold text-red-500 text-center" style={{
          textShadow: '0 0 30px rgba(239, 68, 68, 0.5)'
        }} data-id="element-108">
            Effectively lost to civilization.
          </p>

          <div className="bg-gradient-to-br from-red-950/30 to-black border-l-4 border-red-600 p-4 sm:p-6 md:p-8 my-4 sm:my-6 md:my-8" data-id="element-109">
            <p className="text-lg sm:text-xl text-stone-100 font-bold" data-id="element-110">
              This loss of centralized knowledge may have set humanity back
              HUNDREDS of years.
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center gap-4 py-12 my-8" data-id="element-111">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-600/50 to-amber-600/50" data-id="element-112" />
            <div className="w-2 h-2 bg-amber-600 rotate-45" data-id="element-113" />
            <div className="w-32 h-px bg-gradient-to-l from-transparent via-amber-600/50 to-amber-600/50" data-id="element-114" />
          </div>

          <p className="text-2xl font-bold text-stone-100" data-id="element-115">The thing is...</p>

          <p className="text-2xl md:text-3xl font-bold text-amber-500 text-center" style={{
          textShadow: '0 0 40px rgba(217, 119, 6, 0.5)'
        }} data-id="element-116">
            Many people don't realize this is happening AGAIN.
          </p>

          <p className="text-stone-400 italic text-center" data-id="element-117">
            (Especially with the constant hype about AI, and how it's been
            trained on "all of humanity's data.")
          </p>

          <p data-id="element-118">
            But more than a few minutes with AI tools should show you the
            constant stream of hallucinations they dress up as "truth."
          </p>

          <p data-id="element-119">
            It's ESPECIALLY noticeable when you talk about less
            universally-known fields of study...
          </p>

          <p className="text-xl font-bold text-stone-100" data-id="element-120">
            Like peptide science.
          </p>

          {/* Big reveal */}
          <div className="bg-gradient-to-br from-amber-950/40 to-black border-2 border-amber-600 p-4 sm:p-6 md:p-10 my-6 sm:my-8 md:my-12 relative" data-id="element-121">
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-amber-600" data-id="element-122" />
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-amber-600" data-id="element-123" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-amber-600" data-id="element-124" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-amber-600" data-id="element-125" />

            <p className="text-2xl md:text-3xl font-bold text-center text-amber-500 leading-relaxed" style={{
            textShadow: '0 0 30px rgba(217, 119, 6, 0.4)'
          }} data-id="element-126">
              Which brings me to my big point:
            </p>
          </div>

          <p className="text-2xl md:text-3xl font-bold text-stone-100 text-center leading-tight" data-id="element-127">
            Right now, when it comes to the science of human health and
            performance?
          </p>

          <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-red-500 text-center" style={{
          textShadow: '0 0 40px rgba(239, 68, 68, 0.6)'
        }} data-id="element-128">
            We are living in a time of massive fragmentation.
          </p>

          {/* Fragmentation explanation */}
          <div className="space-y-4 pl-6 border-l-2 border-amber-600/30 my-8" data-id="element-129">
            <p className="text-stone-100 font-semibold" data-id="element-130">
              Because the knowledge exists.
            </p>
            <p className="text-stone-100 font-semibold" data-id="element-131">
              And the solutions are out there.
            </p>
            <p className="text-xl font-bold text-amber-500" data-id="element-132">
              But they are scattered.
            </p>
          </div>

          <div className="space-y-4 text-stone-300" data-id="element-133">
            <p data-id="element-134">
              Some of it is locked behind language barriers in obscure Russian
              military journals.
            </p>
            <p data-id="element-135">
              Some of it is buried in dense academic papers that no one reads.
            </p>
            <p data-id="element-136">
              Some of it is hidden behind the dogma of a medical system that
              focuses on "management" rather than "cure."
            </p>
            <p data-id="element-137">
              Some of it only exists in the minds of a few people who've
              dedicated their lives to mastering it.
            </p>
          </div>
        </div>
      </motion.div>
    </section>;
}